import type { Lang } from "../../lib/i18n";

/** Per-career-path accent colors (admin charts & badges). */
export const PATH_COLORS: Record<string, string> = {
  finance: "#10b981",
  procurement: "#3b82f6",
  "project-management": "#a855f7",
  "assets-management": "#f59e0b",
  "hr-admin": "#ec4899",
  commercial: "#06b6d4",
  corporate: "#8b5cf6",
  development: "#ef4444",
  "": "#94a3b8",
};

export function pathColor(path: string | undefined): string {
  return PATH_COLORS[path || ""] || PATH_COLORS[""];
}

export function formatDateTime(ts: number, lang: Lang): string {
  return new Date(ts).toLocaleString(lang === "ar" ? "ar-SA" : "en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDuration(ms: number | undefined, lang: Lang): string {
  if (!ms) return "—";
  const min = Math.floor(ms / 60000);
  const sec = Math.floor((ms % 60000) / 1000);
  return lang === "ar" ? `${min} د ${sec} ث` : `${min}m ${sec}s`;
}

/** Relative "time ago" (e.g. "منذ 5 د" / "5m ago"). */
export function relativeTime(ts: number, lang: Lang): string {
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (lang === "ar") {
    if (sec < 60) return "الآن";
    if (min < 60) return `منذ ${min} د`;
    if (hr < 24) return `منذ ${hr} س`;
    if (day < 30) return `منذ ${day} يوم`;
    return new Date(ts).toLocaleDateString("ar-SA");
  }
  if (sec < 60) return "just now";
  if (min < 60) return `${min}m ago`;
  if (hr < 24) return `${hr}h ago`;
  if (day < 30) return `${day}d ago`;
  return new Date(ts).toLocaleDateString("en-US");
}

/** Audit action → label (bilingual), color, and icon. */
export const AUDIT_ACTIONS: Record<string, { ar: string; en: string; color: string; icon: string }> = {
  admin_login: { ar: "دخول إداري", en: "Admin login", color: "#3b82f6", icon: "🔑" },
  admin_logout: { ar: "خروج إداري", en: "Admin logout", color: "#94a3b8", icon: "🚪" },
  credentials_changed: { ar: "تغيير بيانات الدخول", en: "Credentials changed", color: "#f59e0b", icon: "🔒" },
  system_added: { ar: "إضافة نظام", en: "System added", color: "#10b981", icon: "➕" },
  system_updated: { ar: "تعديل نظام", en: "System updated", color: "#3b82f6", icon: "✏️" },
  system_deleted: { ar: "حذف نظام", en: "System deleted", color: "#ef4444", icon: "🗑" },
  system_toggled: { ar: "تفعيل/تعطيل نظام", en: "System toggled", color: "#a855f7", icon: "🔀" },
  team_member_added: { ar: "إضافة عضو فريق", en: "Team member added", color: "#10b981", icon: "👤" },
  team_member_updated: { ar: "تعديل عضو فريق", en: "Team member updated", color: "#3b82f6", icon: "✏️" },
  team_member_deleted: { ar: "حذف عضو فريق", en: "Team member deleted", color: "#ef4444", icon: "🗑" },
  smtp_updated: { ar: "تحديث إعدادات البريد", en: "SMTP updated", color: "#06b6d4", icon: "✉️" },
  smtp_test_ok: { ar: "اختبار بريد ناجح", en: "SMTP test OK", color: "#10b981", icon: "✅" },
  smtp_test_fail: { ar: "فشل اختبار البريد", en: "SMTP test failed", color: "#ef4444", icon: "❌" },
  qa_added: { ar: "إضافة سؤال", en: "Q&A added", color: "#10b981", icon: "➕" },
  qa_updated: { ar: "تعديل سؤال", en: "Q&A updated", color: "#3b82f6", icon: "✏️" },
  qa_deleted: { ar: "حذف سؤال", en: "Q&A deleted", color: "#ef4444", icon: "🗑" },
  analytics_cleared: { ar: "مسح التحليلات", en: "Analytics cleared", color: "#ef4444", icon: "🧹" },
  config_imported: { ar: "استيراد إعدادات", en: "Config imported", color: "#a855f7", icon: "📥" },
  config_exported: { ar: "تصدير إعدادات", en: "Config exported", color: "#94a3b8", icon: "📤" },
  reset_all: { ar: "إعادة تعيين كاملة", en: "Full reset", color: "#ef4444", icon: "⟲" },
};
